import React, { useEffect, useState } from "react";

// Semantic UI React
import {
  Icon,
  Menu,
  MenuItem,
  Segment,
  Sidebar,
  Button,
  Checkbox,
} from "semantic-ui-react";

// Styling
import "semantic-ui-css/semantic.min.css";
import "./Style.css";

// ESRI
import Map from "@arcgis/core/Map";
import MapView from "@arcgis/core/views/MapView";
import Extent from "@arcgis/core/geometry/Extent";
import esriId from "@arcgis/core/identity/IdentityManager";
import TileLayer from "@arcgis/core/layers/TileLayer";
import FeatureLayer from "@arcgis/core/layers/FeatureLayer.js";
import Query from "@arcgis/core/rest/support/Query.js";

// Widgets
import ZoomWidget from "../GISMappingApp/MapComponents/ZoomWidget";
import SearchWidget from "../Shared/components/SearchWidget";

var default_extent = new Extent({
  xmin: 2170244.54269575,
  ymin: 257059.937828542,
  xmax: 2421112.5982513,
  ymax: 408622.43782854,
  spatialReference: {
    wkid: 3418,
  },
});

// Create map
const MapApp = ({ config }) => {
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState("search");
  const [view, setView] = useState(null);

//   console.log("Map", { config, view });

  useEffect(() => {
    // Load ESRI's IdentifyManager and add the token
    esriId.registerToken({
      server: config.server_url,
      token: config.token,
    });

    var basemap = new TileLayer({
      url: config.basemap_url,
      visible: true,
      id: "basemap",
      title: "basemap",
      minScale: 409600,
      maxScale: 200,
    });

    var sc = new TileLayer({
      url: config.sc_url,
      id: "Surrounding Counties",
      title: "Surrounding Counties",
      visible: true,
      minScale: 409600,
      maxScale: 200,
      // @ts-ignore
      minZoom: 0,
      maxZoom: 14,
      index: 1,
    });

    var road_labels = new TileLayer({
      url: config.road_url,
      id: "Road Labels",
      title: "Road Labels",
      visible: true,
      minScale: 409600,
      maxScale: 200,
      // @ts-ignore
      minZoom: 0,
      maxZoom: 14,
      index: 1,
    });

    const parcel_template = {
      // autocasts as new PopupTemplate()
      title: "Parcel: {PARCEL_NO}",
      content: (feature: any) => {
        var attr = feature.graphic.attributes;
        // console.log({ feature, attr });
        return `
        <table class="esri-widget__table" summary="List of attributes and values">
          <tbody>
            <tr>
              <th style="width: 25%">Owner</th>
              <td>${attr.OwnerName}</td>
            </tr>
            <tr>
              <th style="width: 25%">Address</th>
              <td>${attr.FullSitus}</td>
            </tr>
          </tbody>
        </table>`;
      },
    };

    const parcels = new FeatureLayer({
      id: "parcels",
      url: config.parcels_url,
      visible: false,
      popupEnabled: true,
      popupTemplate: parcel_template,
      outFields: ["PARCEL_NO", "OwnerName", "FullSitus"],
    });

    const septic_template = {
      // autocasts as new PopupTemplate()
      title: "Septic Permit",
      content: (feature: any) => {
        var attr = feature.graphic.attributes;
        // console.log({ feature, attr });
        return `
        <table class="esri-widget__table" summary="List of attributes and values">
          <tbody>
            <tr>
              <th style="width: 25%">Address</th>
              <td>${attr.FullAddres}</td>
            </tr>
            <tr>
              <th style="width: 25%">Type</th>
              <td>${attr.Type}</td>
            </tr>
          </tbody>
        </table>
        <hr />
        <a href="${attr.Image}"} target="_blank" class="ui primary fluid small button">
        <i class="external icon"></i>
        View File
      </a>`;
      },
    };

    const septic_permits = new FeatureLayer({
      id: "septic_permits",
      url: config.septic_url,
      popupEnabled: true,
      popupTemplate: septic_template,
      outFields: ["FullAddres", "Type", "Image"],
    });

    let map = new Map({
      layers: [basemap, sc, road_labels, parcels, septic_permits],
    });

    let mapView = new MapView({
      container: "map-container",
      map: map,

      // @ts-ignore
      extent: default_extent,
      constraints: {
        rotationEnabled: false,
        snapToZoom: false,
        minScale: 409600,
        maxScale: 200,
        minZoom: 0,
        maxZoom: 11,
        lods: [
          // @ts-ignore
          { level: 0, resolution: 355.55555555555554, scale: 409600 },
          // @ts-ignore
          { level: 1, resolution: 177.77777777777777, scale: 204800 },
          // @ts-ignore
          { level: 2, resolution: 88.88888888888889, scale: 102400 },
          // @ts-ignore
          { level: 3, resolution: 44.44444444444444, scale: 51200 },
          // @ts-ignore
          { level: 4, resolution: 22.22222222222222, scale: 25600 },
          // @ts-ignore
          { level: 5, resolution: 11.11111111111111, scale: 12800 },
          // @ts-ignore
          { level: 6, resolution: 5.555555555555555, scale: 6400 },
          // @ts-ignore
          { level: 7, resolution: 2.7777777777777777, scale: 3200 },
          // @ts-ignore
          { level: 8, resolution: 1.3888888888888888, scale: 1600 },
          // @ts-ignore
          { level: 9, resolution: 0.6944444444444444, scale: 800 },
          // @ts-ignore
          { level: 10, resolution: 0.3472222222222222, scale: 400 },
          // @ts-ignore
          { level: 11, resolution: 0.1736111111111111, scale: 200 },
          // @ts-ignore
        ],
      },
    });
    // @ts-ignore
    mapView.popup.popupEnabled = true;
    mapView.popup.dockOptions = {
      buttonEnabled: false,
      // breakpoint: false,
    };
    mapView.popup.dockEnabled = false;
    mapView.popup.highlightEnabled = true;

    mapView.when(() => {
      // @ts-ignore
      setView(mapView);
    });
  }, [config]);

  const toggleVisibility = (i: any) => {
    // Find the layer based on the id
    // @ts-ignore
    const match = view.map.layers.items.find(
      (layer: any) => layer.id === i.name
    );

    // Set the visibility
    match.visible = i.checked;
  };

  const searchCallback = (record: any) => {
    // console.log("searchCallback", { record });

    // Check if the width of the app is less than 800 and if so then close the sidebar
    if (window.innerWidth <= 800) {
      setSidebarVisible(false);
    }

    const queryObj = new Query();
    queryObj.returnGeometry = true;
    queryObj.outFields = ["*"];
    queryObj.where = `${record.field} = '${record.value}'`;

    // @ts-ignore
    const match = view.map.layers.items.find(
      (layer: any) => layer.id === "parcels"
    );

    // @ts-ignore
    view.closePopup();

    match.queryFeatures(queryObj).then(function (results: any) {
      // prints the array of result graphics to the console
      console.error(results.features);

      // @ts-ignore
      view.goTo(results.features[0]);

      // @ts-ignore
      view.openPopup({
        location: results.features[0].geometry.centroid,
        features: results.features, // array of graphics or a single graphic in an array
      });
    });
  };

  return (
    <>
      <Sidebar.Pushable
        as={Segment}
        id="site-pushable"
        attached
        style={{ height: "100%" }}
      >
        <Sidebar
          width={"wide"}
          as={Segment}
          direction="right"
          animation="overlay"
          onHide={() => setSidebarVisible(false)}
          vertical
          visible={sidebarVisible}
          style={{
            maxWidth: "100%",
            textAlign: "left",
            backgroundColor: "#FFFFFF",
            padding: 0,
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            overflow: "hidden",
          }}
        >
          <Menu tabular style={{ margin: 0 }}>
            <MenuItem
              name="search"
              active={activeMenuItem === "search"}
              onClick={() => setActiveMenuItem("search")}
            />
            <MenuItem
              name="layers"
              active={activeMenuItem === "layers"}
              onClick={() => setActiveMenuItem("layers")}
            />
          </Menu>
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              border: "1px solid #d4d4d5",
              borderTop: "none",
              padding: "0.5em",
              overflow: "hidden",
            }}
          >
            <Button
              icon
              onClick={() => setSidebarVisible(false)}
              style={{ position: "absolute", top: "4px", right: "2px" }}
            >
              <Icon name="remove" />
            </Button>
            {activeMenuItem === "search" && (
              <SearchWidget
                service={config.parcels_url}
                result_callback={searchCallback}
              />
            )}
            {activeMenuItem === "layers" && (
              <div>
                <Checkbox
                  name="parcels"
                  style={{ width: "100%", marginBottom: "10px" }}
                  label={"Parcels"}
                  // @ts-ignore
                  onClick={(e, i) => toggleVisibility(i)}
                />
                <Checkbox
                  defaultChecked={true}
                  name="septic_permits"
                  style={{ width: "100%", marginBottom: "10px" }}
                  label={"Septic Permits"}
                  // @ts-ignore
                  onClick={(e, i) => toggleVisibility(i)}
                />
              </div>
            )}
          </div>
        </Sidebar>
        <Sidebar.Pusher id="site-pusher">
          <Segment id="site-container">
            <div id="map-container">
              {view && (
                <>
                  <div className="top-left map-buttons">
                    {view && <ZoomWidget view={view} />}
                  </div>
                  <div className="top-right map-buttons">
                    <Button icon onClick={() => setSidebarVisible(true)}>
                      <Icon name="list" />
                    </Button>
                  </div>
                </>
              )}
            </div>
          </Segment>
        </Sidebar.Pusher>
      </Sidebar.Pushable>
    </>
  );
};

export default MapApp;
